import React, { useMemo } from 'react';
import { useFinanceEntity } from './financeEntity';
import { useTranslation } from 'react-i18next';
import { StoreContext } from './StoreContext';
import { useLegalEntities } from './legalEntities';
import { useBankAccounts } from './bankAccounts';
import { usePayoutProfiles } from './payoutProfiles';
import PropTypes from 'prop-types';
import { Organization } from '../../../../models';

export const StoreProvider = ({ organizationId, children }) => {
    const { t } = useTranslation();

    const financeEntity = useFinanceEntity(organizationId, {
        successUpdateToast: t('sauron.financeModule.financeEntity.updateSuccessToast')
    });

    const [organization, loadingOrganization] = Organization.useApiModel(
        {
            id: organizationId
        },
        [organizationId]
    );

    const legalEntities = useLegalEntities(financeEntity.id);
    const bankAccounts = useBankAccounts(financeEntity.id);
    const payoutProfiles = usePayoutProfiles(financeEntity.id);

    const value = useMemo(() => {
        const isBankAccountDeletable = id => {
            const linkedPayoutProfiles = payoutProfiles.data.filter(profile => profile.bank_account_id === id);
            return linkedPayoutProfiles.length === 0;
        };

        const isLegalEntityDeletable = id => {
            const linkedPayoutProfiles = payoutProfiles.data.filter(profile => profile.document_head_id === id);
            return linkedPayoutProfiles.length === 0;
        };

        return {
            financeEntity,
            legalEntities,
            bankAccounts,
            payoutProfiles,
            isLegalEntityDeletable,
            isBankAccountDeletable,
            currency: {
                isLoading: loadingOrganization,
                value: organization?.currency || null
            }
        };
    }, [financeEntity, legalEntities, bankAccounts, payoutProfiles]);

    return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
};

StoreProvider.propTypes = {
    organizationId: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired
};
